import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { createContext } from "react";
import type { Permission } from "src/api/Tmd.dtos";

export enum LinkPermissionMode {
    All = 1,
    Any = 2,
}

export enum LayoutPermissionMode {
    Exact = 1,
    Any = 2,
}

export interface ILayoutItemContext {
    trialId?: number;
    institutionId?: number;
    trialInstitutionId?: number;
    departmentId?: number;
    patientId?: number;
    groupId?: number;
    personnelId?: number;
}

export interface ILayoutLink {
    icon: IconDefinition;
    label?: string;
    href: string;
    actionFn?: () => void;
    newTab?: boolean;
    permissions?: Permission[];
    permissionMode?: LinkPermissionMode;
    className?: string;
    style?: React.CSSProperties;
}

export interface ILayoutSubMenu {
    code: string;
    title?: string;
    context?: ILayoutItemContext;
    items: ILayoutLink[];
}

export interface ILayoutContext {
    menuItems?: ILayoutLink[];
    subMenus?: ILayoutSubMenu[];
    restricted?: boolean;
    permission?: Permission;
    permissionMode?: LayoutPermissionMode;
    context: ILayoutItemContext;
    loading?: boolean;
}

export interface ILayoutActionsContext {
    setMenuItems: (menuItems: ILayoutLink[]) => void;
    setSubMenus: (subMenus: ILayoutSubMenu[]) => void;
    addSubMenu: (subMenu: ILayoutSubMenu) => void;
    removeSubMenu: (subMenu: ILayoutSubMenu) => void;
    addSubMenuByCode: (code: string) => void;
    removeSubMenuByCode: (code: string) => void;
    setRestricted: (restricted: boolean) => void;
    setPermission: (permission: Permission) => void;
    setPermissionMode: (permissionMode: LayoutPermissionMode) => void;
    setLoading: (loading: boolean) => void;
    setContext: (context: ILayoutItemContext) => void;
}

export const defaultLayoutActionsContext: ILayoutActionsContext = {
    setMenuItems: (menuItems: ILayoutLink[]) => {},
    setSubMenus: (subMenus: ILayoutSubMenu[]) => {},
    addSubMenu: (subMenu: ILayoutSubMenu) => {},
    removeSubMenu: (subMenu: ILayoutSubMenu) => {},
    addSubMenuByCode: (code: string) => {},
    removeSubMenuByCode: (code: string) => {},
    setRestricted: (restricted: boolean) => {},
    setPermission: (permission: Permission) => {},
    setPermissionMode: (permissionMode: LayoutPermissionMode) => {},
    setLoading: (loading: boolean) => {},
    setContext: (context: ILayoutItemContext) => {},
};

export const defaultLayoutContext: ILayoutContext = {
    menuItems: [],
    subMenus: [],
    restricted: false,
    permission: undefined,
    permissionMode: LayoutPermissionMode.Exact,
    loading: false,
    context: {},
};

export const LayoutContext = createContext<ILayoutContext>(defaultLayoutContext);

export const LayoutActionsContext = createContext<ILayoutActionsContext>(
    defaultLayoutActionsContext,
);

export default LayoutContext;
